import { clientColumns, TableSettings, useTableSettings, withTableSettings } from '../../features';
import {
  ClientFilter,
  DataGrid,
  FilterBadges,
  ListPageWrapper,
  PageTitle, TabLabel, useAgent,
  useClients, useSessionStorageState
} from '../../shared';
import { useClientListFilterDrawer, withClientListFilterDrawer } from '../clients/ui/client-list-filter-drawer';
import { useClientsToCall } from '../clients/hooks/use-clients-to-call';
import {
  ClientViewFilterInput,
  ClientViewSortInput,
  Field,
  PageInput,
  SortDirection
} from '@coverright/data-access/types/enrollment';
import _ from 'lodash';
import { Button, Stack, Tab as MuiTab, TabProps, Tabs } from '@mui/material';
import { SearchInput } from '@coverright/ui/inputs';
import { FunnelSimple } from '@phosphor-icons/react';
import * as React from 'react';
import { Link, LinkProps, useParams } from 'react-router-dom';
import { getRoute } from '../../app/router';

const Page = () => {
  const {partner = 'nerdWallet'} = useParams<{partner: string}>();

  const [filters, setFilters] = useFilters(partner);
  const {columns} = useTableSettings(clientColumns, filters, settings => setFilters(settings.filters));
  const [page, setPage] = usePage(partner);
  const [sort, setSort] = useSort(partner);
  const {clients, loading, total} = useClients(page, filters, sort, partner === 'others' ? 'otherpartner' : partner);
  const {open} = useClientListFilterDrawer();
  const agent = useAgent();

  const clientsToCall = useClientsToCall();

  const openFilters = () => open(v => {
    setFilters(v)
  }, filters)

  const onFilterRemoveClick = (filterName: keyof ClientViewFilterInput) => {
    setFilters(prev => {
      setPage(p => ({...p, page: 0}));
      return _.omit(prev, filterName)
    })
  }

  return (
    <ListPageWrapper>
      <Tabs value={partner} sx={{flex: 1, mb: 2, minHeight: 35}}>
        <Tab to={`/${getRoute(`partnerLeads`)}/nerdWallet`} value={'nerdWallet'} label={<TabLabel>NerdWallet</TabLabel>} />
        <Tab to={`/${getRoute(`partnerLeads`)}/wegmans`} value={'wegmans'} label={<TabLabel>Wegmans</TabLabel>} />
        <Tab to={`/${getRoute(`partnerLeads`)}/boldin`} value={'boldin'} label={<TabLabel>Boldin</TabLabel>} />
        <Tab to={`/${getRoute(`partnerLeads`)}/healthcare`} value={'healthcare'} label={<TabLabel>Healthcare.com</TabLabel>} />
        <Tab to={`/${getRoute(`partnerLeads`)}/workmoney`} value={'workmoney'} label={<TabLabel>WorkMoney</TabLabel>} />
        <Tab to={`/${getRoute(`partnerLeads`)}/sheridan`} value={'sheridan'} label={<TabLabel>Sheridan</TabLabel>} />
        <Tab to={`/${getRoute(`partnerLeads`)}/wealthenhancement`} value={'wealthenhancement'} label={<TabLabel>Wealth Enhancement</TabLabel>} />
        <Tab to={`/${getRoute(`partnerLeads`)}/quinstreet`} value={'quinstreet'} label={<TabLabel>QuinStreet</TabLabel>} />
        <Tab to={`/${getRoute(`partnerLeads`)}/others`} value={'others'} label={<TabLabel>Others</TabLabel>} />
      </Tabs>

      <PageTitle title={`${_.upperFirst(partner)} clients`}>
        <TableSettings />
        <Stack direction={'row'} gap={2}>
          <SearchInput value={filters?.searchTerm || ''}
                       loading={loading}
                       onChange={searchTerm => setFilters(p => ({...p, searchTerm}))} />
          <Button variant={'outlined'}
                  data-test={'clients-filter-button'}
                  onClick={openFilters}
                  startIcon={<FunnelSimple weight={'bold'} />}
                  size={'small'} sx={{minWidth: 90, alignSelf: 'center'}}>
            Filters
          </Button>
        </Stack>
      </PageTitle>

      <FilterBadges onRemoveFilterClick={onFilterRemoveClick} filter={filters} nameMapFunc={filterNameFunc} />

      <DataGrid columns={columns}
                page={page.page}
                rowsPerPage={page.size}
                total={total}
                getRowClassName={row => (
                  clientsToCall.some(c => c.clientId === row.clientId) ?
                    clientsToCall.find(c => c.clientId === row.clientId)?.currentAssignment?.assignedAgentId === agent?.id ? 'assigned-client-data-grid-row' : 'assigned-client-to-other-agent-data-grid-row'
                    : ''
                )}
                loading={loading}
                sort={sort}
                onSortModelChange={setSort}
                onPageChange={p => setPage(prev => ({...prev, page: p }))}
                onRowsPerPageChange={p => setPage(prev => ({...prev, size: p }))}
                rows={clients}/>
    </ListPageWrapper>
  );
};

export const PartnerLeads = withClientListFilterDrawer(withTableSettings(() => {
  const {partner = 'nerdWallet'} = useParams<{partner: string}>();
  return <Page key={partner} />
}, 'partnerLeads'));

const filterNameFunc = (name: keyof ClientFilter) => {
  switch (name) {
    case 'followUpDate': return 'Next contact date';
    case 'range': return 'Created';
    default: return name;
  }
}

const useFilters = (prefix: string) => {
  return useSessionStorageState<ClientFilter | undefined>(
    prefix + 'Filters',
    undefined
  );
};

const usePage = (prefix: string) => {
  return useSessionStorageState<PageInput>(
    prefix + 'Page',
    { page: 0, size: 20 }
  );
};

const useSort = (prefix: string) => {
  return useSessionStorageState<ClientViewSortInput | undefined>(
    prefix + 'Sort',
    {field: Field.CreatedAt, direction: SortDirection.Desc}
  )
}

const Tab = (props: TabProps & LinkProps) => (
  <MuiTab component={Link} iconPosition={'start'} {...props} />
)
